import { liquidoDe } from '../finance/abatimento.js'
import { IN_COMPETENCE } from '../finance/cash-impact.js'
import type { Category, Transaction } from '../finance/index.js'
import type { Cents } from '../../shared/money/cents.js'
import type { ReferenceMonth } from '../../shared/date/reference-month.js'

/**
 * As agregações de `Docs/05-Plano/Fase 11 - Relatórios.md`.
 *
 * ## A lente atravessa TODOS os eixos, e não é um filtro a mais
 *
 * *"Competência e caixa não são um filtro a mais — são a lente que atravessa
 * todos os outros. Todo relatório declara qual está usando, sempre visível."*
 *
 * Por isso `Lens` é **parâmetro obrigatório** de toda função aqui, e não uma
 * opção com padrão: um relatório que escolhesse a lente sozinho seria um número
 * sem pergunta. Quem chama tem de dizer qual, e a tela tem de mostrar.
 *
 * ## O total SEMPRE bate com a lista que o gerou
 *
 * É a terceira linha da definição de pronto, e ela é uma consequência de
 * desenho, não de disciplina: todas as funções abaixo partem da **mesma** lista
 * já recortada, e a soma de qualquer agrupamento é a soma dela. Não há um
 * "total" calculado por outro caminho que pudesse divergir.
 *
 * ## O que sai e o que fica
 *
 * - **`canceled` sai** — não é gasto, e é a mesma exclusão de `spendEntriesOf`.
 * - **`transfer` sai** — dinheiro entre contas próprias não é consumo, como em
 *   todo relatório de gasto desta casa.
 * - **Entrada sai dos relatórios de GASTO.** Um ranking de categorias que
 *   incluísse o salário teria "Salário" no topo de um relatório chamado "onde
 *   foi o dinheiro".
 *
 * ## Sem agregado por Cloud Function, e a medição é o motivo
 *
 * A nota da fase prevê `households/{hid}/aggregates/{YYYY-MM}` mantido por
 * Function, e ela mesma diz por quê: *"o agregado existe pela latência da tela,
 * não por limite de escala"*. Medido na base real, o maior mês tem **91**
 * transações (decisão 144a) — doze meses são ~1.100 documentos, lidos em doze
 * consultas paralelas. Um agregado seria uma segunda fonte de verdade sobre
 * dinheiro, com uma Function para mantê-la, para ganhar uma latência que já não
 * existe. Reabre quando um mês passar da casa dos milhares.
 */

export type Lens = 'competence' | 'cash'

/** Uma fatia de relatório: um rótulo, um valor, e de onde ele veio. */
export type Slice = {
  key: string
  label: string
  /** Em módulo — relatório de gasto desenha grandeza. */
  cents: Cents
  /** Quantas transações formaram este número. */
  count: number
}

/**
 * As transações que a lente enxerga, já limpas.
 *
 * **Recebe as duas listas e escolhe**, em vez de receber uma já escolhida: assim
 * a escolha da lente é visível num lugar só, e trocá-la na tela não pode
 * esquecer de trocar a fonte junto.
 */
export function throughLens(
  competence: Transaction[],
  cash: Transaction[],
  lens: Lens,
  categories: Category[],
): Transaction[] {
  const transferIds = new Set(
    categories.filter((c) => c.kind === 'transfer').map((c) => c.id),
  )
  return (lens === 'competence' ? competence : cash).filter((t) => {
    if (t.status === 'canceled') return false
    const categoryId = t.enrichment.categoryId
    if (categoryId !== null && transferIds.has(categoryId)) return false
    // A lente de competência já vem recortada pelo repositório, mas um
    // `settlement` que escapasse por outro caminho não é consumo. Barato, e
    // fecha a porta.
    if (lens === 'competence' && !IN_COMPETENCE[t.cashImpact]) return false
    return true
  })
}

/**
 * O valor de um gasto no relatório, em módulo. Na competência é o LÍQUIDO da
 * compra — o que as entradas abateram não foi consumo da casa; no caixa, o que
 * saiu da conta, que não muda com abatimento nenhum. Ver
 * `Docs/05-Plano/Abatimento de gasto.md`.
 */
export function valorDoGasto(t: Transaction, lens: Lens): Cents {
  return Math.abs(lens === 'competence' ? liquidoDe(t) : t.source.amountCents)
}

/**
 * Só as saídas. Ver "o que sai e o que fica" no cabeçalho. Na competência, só
 * as que sobraram depois do abatimento: a compra que as entradas zeraram não
 * é gasto da casa.
 */
export function onlySpending(transactions: Transaction[], lens: Lens): Transaction[] {
  return transactions.filter((t) =>
    lens === 'competence' ? liquidoDe(t) < 0 : t.source.amountCents < 0,
  )
}

/**
 * Agrupa por uma chave qualquer. É a base dos cinco eixos, e existe para eles
 * não terem cinco somas parecidas escritas separadamente.
 *
 * `keyOf` devolve `null` para "não classificado", que vira uma fatia própria com
 * o rótulo dado — e **não** é descartado: uma fatia que some faz o total do
 * relatório parar de bater com a lista, que é a definição de pronto quebrada.
 */
export function groupBy(
  transactions: Transaction[],
  keyOf: (t: Transaction) => string | null,
  labelOf: (key: string) => string,
  unclassifiedLabel: string,
  lens: Lens,
): Slice[] {
  const somas = new Map<string | null, { cents: number; count: number }>()
  for (const t of transactions) {
    const key = keyOf(t)
    const atual = somas.get(key) ?? { cents: 0, count: 0 }
    somas.set(key, {
      cents: atual.cents + valorDoGasto(t, lens),
      count: atual.count + 1,
    })
  }

  return [...somas.entries()]
    .map(([key, { cents, count }]) => ({
      key: key ?? '',
      label: key === null ? unclassifiedLabel : labelOf(key),
      cents,
      count,
    }))
    // Maior primeiro; a chave desempata para a ordem ser estável entre
    // execuções — sem isso, dois eixos empatados trocariam de lugar a cada
    // renderização.
    .sort((a, b) => b.cents - a.cents || a.key.localeCompare(b.key))
}

/** A raiz da árvore de uma categoria, para o eixo agrupar no primeiro nível. */
export function rootCategoryOf(categoryId: string, byId: Map<string, Category>): string | null {
  const visto = new Set<string>()
  let atual = byId.get(categoryId)
  while (atual !== undefined && atual.parentId !== null) {
    // A árvore pode receber um ciclo — `wouldCreateCycle` existe por isso.
    if (visto.has(atual.id)) return null
    visto.add(atual.id)
    atual = byId.get(atual.parentId)
  }
  return atual?.id ?? null
}

/** A soma de um conjunto de fatias. É o total que a tela mostra. */
export function totalOf(slices: Slice[]): Cents {
  return slices.reduce((soma, s) => soma + s.cents, 0)
}

/** Um ponto da evolução mensal. */
export type MonthlyPoint = {
  month: ReferenceMonth
  cents: Cents
  count: number
}

/**
 * A evolução mês a mês.
 *
 * **Mês sem transação vira um ponto de ZERO, e não um buraco.** Um gráfico que
 * pula o mês vazio desenha uma linha que sobe onde ela deveria cair — e é
 * exatamente no mês em que não se gastou nada que a pessoa quer ver o vale.
 */
export function monthlySeries(
  byMonth: Map<ReferenceMonth, Transaction[]>,
  months: ReferenceMonth[],
  lens: Lens,
): MonthlyPoint[] {
  return months.map((month) => {
    const doMes = byMonth.get(month) ?? []
    return {
      month,
      cents: doMes.reduce((soma, t) => soma + valorDoGasto(t, lens), 0),
      count: doMes.length,
    }
  })
}

/**
 * Variação de um ponto para o mesmo mês do ano anterior.
 *
 * `null` quando não há o ano anterior — e não zero: *"o app é honesto sobre não
 * saber"*. Doze meses de histórico não são um ano de comparação.
 */
export function yearOverYear(
  points: MonthlyPoint[],
  month: ReferenceMonth,
): { atual: Cents; anterior: Cents } | null {
  const atual = points.find((p) => p.month === month)
  const anoAnterior = `${String(Number(month.slice(0, 4)) - 1)}${month.slice(4)}`
  const anterior = points.find((p) => p.month === anoAnterior)
  if (atual === undefined || anterior === undefined) return null
  return { atual: atual.cents, anterior: anterior.cents }
}
